import React, { MouseEventHandler, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Control } from 'react-hook-form/dist/types/form';
import PopupServiceImg from 'Assets/layout/dashboard/popup-service.png';
import DefaultModal from 'Douleutaras/components/modals/DefaultModal';
import Modal from 'Douleutaras/components/Modal/Modal';
import ModalSelectionItem from 'Douleutaras/components/modals/parts/ModalSelectionItem';
import { CustomLabel } from 'Douleutaras/components/CustomLabel/CustomLabel';
import { CustomInput } from 'Douleutaras/components/CustomInput/CustomInput';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { jobBidsSelectors } from 'Douleutaras/redux/data/jobBids/jobBidsSlice';
import RegularButton from 'Douleutaras/components/RegularButton/RegularButton';
import { bidBuyerStatusEndpoint, getBidBuyerStatus } from 'Douleutaras/redux/data/jobBids/jobBidsStatusBuyerSlice';
import { jobSelectors } from 'Douleutaras/redux/data/jobs/jobsSlice';
import { QuickCheckbox } from 'Douleutaras/components/QuickCheckbox/QuickCheckbox';
import CustomError from 'Douleutaras/components/CustomError/CustomError';
import { CURRENCY } from 'Douleutaras/types/index';

import './QuoteDetailsModal.scss';

interface QuoteDetailsModalProps {
  isOpen: boolean;
  title: string | React.ReactElement;
  jobId: string;
  note: string | React.ReactElement;
  bidId: string;
  isInvalid: boolean;
  isSubmitting: boolean;
  control: Control;
  userMugshot?: string;
  onSubmitClick: MouseEventHandler<Element>;
  onBack?(): void;
  setIsOpen(modalStatus): void;
}

export const QuoteDetailsModal = ({
                               bidId,
                               isOpen,
                               jobId,
                               setIsOpen,
                               title,
                               onBack,
                               isInvalid,
                               isSubmitting,
                               control,
                               onSubmitClick,
                               userMugshot
                             }: QuoteDetailsModalProps) => {
  const currentBid = useAppSelector(state => jobBidsSelectors.selectById(state, bidId));
  const currentJob = useAppSelector(state => jobSelectors.selectById(state, jobId));
  const priceDefiningQuestions = useAppSelector(state => state.jobPriceDefiningQuestions);
  const priceDefiningQnaChoicesIds = currentJob.relationships.priceDefiningQnaChoices.data.map(choice => choice.id);
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} dismissCallback={() => setIsOpen(false)}>

        <DefaultModal
          serviceIcon={PopupServiceImg}
          serviceIconAlt={<Trans>T.CCF.QUOTE DETAILS MODAL.IMAGE ALT TEXT</Trans>}
          title={title}
          backText={<Trans>T.CCF.QUOTE DETAILS MODAL.BACK BUTTON TEXT</Trans>}
          backTextAriaLabel={<Trans>T.CCF.QUOTE DETAILS MODAL.BACK BUTTON TEXT</Trans>}
          closeAriaLabel={<Trans>T.CCF.QUOTE DETAILS MODAL.CLOSE ICON LABEL</Trans>}
          closeButtonCallback={() => setIsOpen(false)}
          backButtonCallback={typeof onBack === 'function' ? onBack : () => false}
          userMugshot={userMugshot}
          showBackButton={typeof onBack === 'function'}
          footer={
            <RegularButton
              disabled={isSubmitting || isInvalid}
              size="large"
              title={<Trans>T.CCF.QUOTE DETAILS MODAL.SAVE LABEL</Trans>}
              onClick={onSubmitClick} />
          }
        >
            <ModalSelectionItem>
              <CustomLabel id="price" title={<Trans>T.CCF.QUOTE DETAILS MODAL.QUOTE AMOUNT LABEL</Trans>} required />
              <Controller
                name="price"
                control={control}
                rules={{
                  required: t('T.CCF.QUOTE DETAILS MODAL.VALIDATE REQUIRED PRICE') as unknown as string,
                  validate: {
                    isNumber: val => (val === undefined || val === '' || !isNaN(val)) || t('T.CCF.QUOTE DETAILS MODAL.VALIDATE PRICE NOT-PRICE'),
                    positive: val => (val === undefined || val === '' || Number(val) >= 0) || t('T.CCF.QUOTE DETAILS MODAL.VALIDATE PRICE NON-POSITIVE'),
                    noComma: val => (val === undefined || String(val)
                      .indexOf(',') === -1) || t('T.CCF.QUOTE DETAILS MODAL.VALIDATE PRICE NO-COMMA'),
                    twoDecimals: val => {
                      if (val === undefined || val === '' || isNaN(val)) {
                        return true;
                      }
                      const s = String(val);
                      if (s.indexOf('.') === -1) {
                        return true;
                      }

                      return countDecimals(Number(val)) <= 2 || t('T.CCF.QUOTE DETAILS MODAL.VALIDATE PRICE TWO-DECIMALS-ONLY');
                    }
                  }
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <>
                    <CustomInput
                      id="price"
                      input={{
                        name: 'price',
                        value: value ?? '',
                        onChange: e => onChange(e?.target?.value)
                      }}
                      contentOnRight
                      autoSelect
                      currency={CURRENCY[currentBid.attributes.currencyCode]}
                    />
                    { error && (
                      <CustomError>{error.message as string}</CustomError>
                    )}
                  </>
                )}
              />
            </ModalSelectionItem>
            <div styleName="ccf-quote-spacer"/>
            { priceDefiningQnaChoicesIds.map(questionId => {
              const question = priceDefiningQuestions.entities[questionId].attributes;
              if (!question.visible) {
                // tslint:disable-next-line:no-null-keyword
                return null;
              }
              switch (question.attribute.datatype) {
                case 'choice':
                  return (
                    <React.Fragment key={`pdq_key_${question.attribute.id}`}>
                      <ModalSelectionItem>
                        <CustomLabel title={question.attribute.title} id={`pdq_label_${question.attribute.id}`} />
                        <Controller
                          name={`priceDefiningAnswers.questionId_${question.attribute.id}`}
                          control={control}
                          rules={{ required: t('T.CCF.QUOTE DETAILS MODAL.VALIDATE REQUIRED ANSWERS') as unknown as string }}
                          render={({ field: { value, onChange, name }, fieldState: { error } }) => (
                            <>
                              {question?.attribute.choices.map(choice => {
                                if (question.conditionLogic !== 'or') {
                                  throw Error('For price defining questions "choice", condition can only work with "or" operator');
                                }
                                const choiceIdStr = String(choice.value.id);

                                return (
                                  <QuickCheckbox
                                    key={`choice_key_${choice.value.id}`}
                                    type="radio"
                                    input={{
                                      name,
                                      value: choiceIdStr,
                                      checked: value === choiceIdStr,
                                      onChange: () => onChange(choiceIdStr)
                                    }}
                                    title={choice.value.title}
                                    testId={`choice_test_${choice.value.id}`}
                                    id={`choice_id_${choice.value.id}`}
                                  />
                                );
                              })}
                              { error && (
                                <CustomError>{error.message as string}</CustomError>
                              )}
                            </>
                          )}
                        />
                      </ModalSelectionItem>
                    </React.Fragment>
                  );
                default:
                  // tslint:disable-next-line:no-null-keyword
                  return null;
              }
            })}
        </DefaultModal>

    </Modal>
  );
};

const countDecimals = value => {
  if (Math.floor(value) === value) {
    return 0;
  }

  return value.toString()
    .split('.')[1].length || 0;
};

const QuoteDetailsModalWrapper = props => {
  const { onSubmit, defaultPrice, defaultAnswers } = props;
  const methods = useForm<{ price?: string; priceDefiningAnswers?: Record<string, string> }>({
    mode: 'onChange',
    defaultValues: useMemo(() => ({ price: defaultPrice, priceDefiningAnswers: defaultAnswers }), [defaultPrice, defaultAnswers])
  });
  const { handleSubmit, control, formState: { isSubmitting, isValid }, reset } = methods;

  useEffect(() => {
    reset({ price: defaultPrice, priceDefiningAnswers: defaultAnswers });
  }, [defaultPrice, defaultAnswers, reset]);

  return (
    <QuoteDetailsModal
      {...props}
      control={control}
      isSubmitting={isSubmitting}
      isInvalid={!isValid}
      onSubmitClick={handleSubmit(onSubmit)}
    />
  );
};

export default props => {
  const { bidId, jobId, isOpen } = props;
  if (!isOpen) {
    // tslint:disable-next-line:no-null-keyword
    return null;
  }
  const currentBid = useAppSelector(state => jobBidsSelectors.selectById(state, bidId));
  const priceDefiningAnswers = useAppSelector(state => state.jobPriceDefiningAnswers);
  const metaKey = bidBuyerStatusEndpoint(jobId, bidId);
  const bidBuyerStatus = useAppSelector(state => state.jobBidsStatusBuyer);
  const dispatch = useAppDispatch();
  const [defaultPrice, setDefaultPrice] = useState<string | undefined>(undefined);
  const [defaultAnswers, setDefaultAnswers] = useState<Record<string, string> | undefined>(undefined);

  const initialize = async () => {
    // Check if the buyer status is fetched, if not, fetch it.
    if (typeof bidBuyerStatus?.meta?.[metaKey] === 'undefined' && isOpen) {
      await dispatch(getBidBuyerStatus({
          jobId,
          bidId
        })
      );
    }

    const priceDefiningAnswerIds = currentBid.relationships.priceDefiningAnswers.data.map(answer => answer.id);
    const answersObj: Record<string, string> = {};
    priceDefiningAnswerIds.forEach(priceDefiningAnswerId => {
      const answer = priceDefiningAnswers.entities[priceDefiningAnswerId];
      const questionId = answer.relationships.attribute.data.id;
      const valueChoiceId = answer.attributes.valueChoice.id;
      answersObj[`questionId_${questionId}`] = String(valueChoiceId);
    });
    setDefaultAnswers(answersObj);

    const currentBuyerSelectionsId = bidBuyerStatus?.meta?.[metaKey]?.data?.[0]?.id;
    const currentBuyerSelections = bidBuyerStatus?.entities?.[currentBuyerSelectionsId]?.attributes;
    const price = currentBuyerSelections?.price ?? currentBid?.attributes.price;

    if (price && parseFloat(price as string) > 0) {
      setDefaultPrice(price as string);
    } else {
      setDefaultPrice(undefined);
    }
  };

  useEffect(() => {
    initialize();
  }, [isOpen]);

  return (
    <QuoteDetailsModalWrapper {...props} defaultPrice={defaultPrice} defaultAnswers={defaultAnswers} />
  );
};
